// HelpCenterPage.jsx
import React, { useState } from "react";
import { Container, Card, Row, Col, Form, Button, Accordion, Badge, Alert } from "react-bootstrap";
import AppShell from "../../components/AppShell";
import api from "../../api";
import { Search, HelpCircle, Mail, BookOpen, Clock, Award, ArrowRight, MessageSquare } from "lucide-react";

const HelpCenterPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [ticket, setTicket] = useState({ subject: "", message: "" });
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    setFeedback("");
    setSubmitting(true);
    try {
      await api.post("/support/ticket", {
        subject: ticket.subject,
        message: ticket.message,
      });
      setFeedback("Thank you! Your support ticket has been submitted to 2234099@students.wits.ac.za. Our team will get back to you shortly.");
      setTicket({ subject: "", message: "" });
    } catch (err) {
      console.error("Failed to submit support ticket:", err);
      setFeedback(err.response?.data?.error || "Failed to submit support ticket. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const categories = [
    {
      icon: <Clock size={22} />,
      title: "Logging Hours",
      count: "5 Articles",
      description: "Learn how to record, track, and verify your volunteer student hours.",
      color: "#1B3A87"
    },
    {
      icon: <BookOpen size={22} />,
      title: "Projects & Operations",
      count: "8 Articles",
      description: "Discover how to browse, register, and initiate community projects.",
      color: "#1B3A87"
    },
    {
      icon: <Award size={22} />,
      title: "Volunteer Outcomes",
      count: "4 Articles",
      description: "Information about reflection posters, certificates, and recognition.",
      color: "#1B3A87"
    }
  ];

  const faqs = [
    {
      question: "How do I submit volunteer hours for approval?",
      answer: "Navigate to the 'My Projects' page from your dashboard sidebar. Select the specific project you volunteered for, fill in the date, start time, end time, and a brief description of what you accomplished, then click 'Log Hours'. The project leader or administrator will review and approve it."
    },
    {
      question: "What is a Reflection Poster?",
      answer: "A Reflection Poster is a visual, creative artifact submitted by students at the end of a project. It summarizes your volunteer experience, core achievements, and the social impact created. Reflection posters can be browsed and displayed directly on the project details page."
    },
    {
      question: "Who approves my logged volunteer hours?",
      answer: "Logged hours are reviewed and approved either by WCCO Administrators or by your designated Project Leaders (students who have been assigned leadership roles for specific volunteer projects)."
    },
    {
      question: "Can I create or pitch my own community project?",
      answer: "Yes, absolutely! WCCO encourages active citizenship. You can propose or initiate a new community engagement project by contacting the WCCO coordinator, or pitching a concept directly to the board through the guidelines document available in the portal."
    }
  ];

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: "16px",
    border: "1px solid #E5E7EB",
    boxShadow: "none",
  };

  return (
    <AppShell>
      <Container 
        fluid 
        className="student-responsive-page p-0"
      >

        {/* Dynamic Search Banner */}
        <Card 
          className="mb-4 p-4 mt-3" 
          style={{ 
            background: "var(--wcco-blue)",
            borderRadius: "16px",
            border: "none",
            color: "#fff",
            boxShadow: "none",
          }}
        >
          <div className="text-center py-4 text-white">
            <h2 className="fw-bold mb-2">WCCO Tech Support</h2>
            <p className="text-white-50 mb-4 fs-5">How can we help you make a difference today?</p>
            <div className="mx-auto position-relative" style={{ maxWidth: "560px" }}>
              <Search className="position-absolute text-muted" style={{ top: "14px", left: "16px" }} size={20} />
              <Form.Control
                type="text"
                placeholder="Search FAQs, articles, and guidelines..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ 
                  borderRadius: "30px", 
                  padding: "12px 20px 12px 48px", 
                  border: "none",
                  boxShadow: "none"
                }}
              />
            </div>
          </div>
        </Card>

        {feedback && (
          <Alert variant="success" dismissible onClose={() => setFeedback("")} className="mb-4 text-start">
            {feedback}
          </Alert>
        )}

        {/* Categories Grid */}
        <Row className="g-4 mb-4">
          {categories.map((cat, index) => (
            <Col lg={4} key={index}>
              <Card style={cardStyle} className="p-4 h-100 text-start">
                <Card.Body className="p-0">
                  <div 
                    className="p-3 rounded-circle d-inline-flex mb-3" 
                    style={{ backgroundColor: `${cat.color}15`, color: cat.color }}
                  >
                    {cat.icon}
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="fw-bold text-dark mb-0">{cat.title}</h5>
                    <Badge bg="secondary" className="px-2 py-1 small" style={{ fontSize: "0.75rem", backgroundColor: "rgba(104, 114, 128, 0.1)", color: "#4B5563" }}>
                      {cat.count}
                    </Badge>
                  </div>
                  <p className="text-secondary small mb-3 leading-relaxed">{cat.description}</p>
                  <span className="text-primary fw-semibold small d-inline-flex align-items-center gap-1 cursor-pointer hover:underline" style={{ color: "#1B3A87", cursor: "pointer" }}>
                    Read Articles <ArrowRight size={14} />
                  </span>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Accordions and Contact Box */}
        <Row className="g-4">
          {/* FAQ Accordions */}
          <Col lg={8}>
            <Card style={cardStyle} className="p-4 text-start">
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="p-2.5 rounded-3" style={{ backgroundColor: "rgba(27, 58, 135, 0.08)", color: "#1B3A87" }}>
                  <HelpCircle size={20} />
                </div>
                <h5 className="fw-bold mb-0" style={{ color: "#1B3A87" }}>Frequently Asked Questions</h5>
              </div>

              {filteredFaqs.length > 0 ? (
                <Accordion className="custom-premium-accordion">
                  {filteredFaqs.map((faq, index) => (
                    <Accordion.Item eventKey={index.toString()} key={index} className="border-0 border-bottom mb-2">
                      <Accordion.Header className="fw-bold py-2">{faq.question}</Accordion.Header>
                      <Accordion.Body className="text-secondary" style={{ lineHeight: "1.6" }}>
                        {faq.answer}
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-4 text-muted">
                  No matches found for "{searchQuery}"
                </div>
              )}
            </Card>
          </Col>

          {/* Helpdesk Ticket submission */}
          <Col lg={4}>
            <Card style={cardStyle} className="p-4 text-start h-100">
              <div className="d-flex align-items-center gap-2 mb-4">
                <div className="p-2.5 rounded-3" style={{ backgroundColor: "rgba(27, 58, 135, 0.08)", color: "#1B3A87" }}>
                  <Mail size={20} />
                </div>
                <h5 className="fw-bold mb-0" style={{ color: "#1B3A87" }}>Contact WCCO Tech Support</h5>
              </div>

              <Form onSubmit={handleTicketSubmit} className="d-flex flex-column h-100 justify-content-between">
                <div>
                  <p className="text-muted small mb-3">
                    Submit your query directly to our administrator: <strong>2234099@students.wits.ac.za</strong>.
                  </p>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold text-secondary small mb-1">Subject</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Issue logging hours"
                      value={ticket.subject}
                      onChange={(e) => setTicket({ ...ticket, subject: e.target.value })}
                      required
                      style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-secondary small mb-1">Detail Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder="Describe your issue or question in detail..."
                      value={ticket.message}
                      onChange={(e) => setTicket({ ...ticket, message: e.target.value })}
                      required
                      style={{ borderRadius: "8px", border: "1px solid #E5E7EB", padding: "10px 14px" }}
                    />
                  </Form.Group>
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  style={{ backgroundColor: "#1B3A87", border: "none", borderRadius: "8px", padding: "12px" }}
                  className="w-100 fw-bold d-flex align-items-center justify-content-center gap-2 mt-auto"
                >
                  <MessageSquare size={16} />
                  {submitting ? "Submitting..." : "Submit Support Ticket"}
                </Button>
              </Form>
            </Card>
          </Col>
        </Row>
      </Container>
    </AppShell>
  );
};

export default HelpCenterPage;
